import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addWorkplace as addWorkplaceCall,
  getWorkplaces,
  removeWorkplace as removeWorkplaceCall,
  restartSupervisor as restartSupervisorCall,
  updateWorkplace as updateWorkplaceCall,
} from 'services/service/admin.service';
import { DEFAULT_APPLIES_WITHIN_MS, DEFAULT_REAP_WITHIN_MS } from '../config/ServiceManager.config';
import { setServiceEnabledOp } from '../ops/ServiceManager.ops';
import type {
  AddWorkplacePayload,
  AddWorkplaceRequest,
  GlobalService,
  SupervisorRestartPayload,
  ToggleOutcome,
  ToggleTarget,
  UpdateWorkplaceRequest,
  Workplace,
  WorkplaceMutationPayload,
  WorkplaceService,
  WorkplacesPayload,
} from '../types/Deployments.types';
import {
  formatDelay,
  isEnabledButNotRunning,
  isLiveStateKnown,
  toggleRowKey,
} from '../utils/ServiceManager.utils';

/**
 * A workplace mutation (add / update / remove) submitted to the supervisor but
 * pending an operator restart to take effect. Renders the restart-required
 * banner.
 *
 * Service toggles are deliberately NOT in this union. They are hot: the
 * supervisor writes the flag, reloads its config and reconciles PM2 inside the
 * request, answering `restartRequired: false`. Telling an operator to restart
 * for a toggle was true of the May-2026 supervisor and has been wrong since the
 * config-to-db cutover.
 *
 * `warnings` is the supervisor's advisory array from the 202 response, most
 * commonly populated when `forceUnsafeRoot` accepted a request and coerced the
 * workplace's `mode` to "standalone".
 */
export type PendingWorkplaceChange =
  | {
      action: 'add';
      /** licensing-DB id we sent (used to recognise the add once supervisor restarts). */
      id: number;
      name: string;
      prefix: string;
      workplaceIndex: number;
      restartHint: string;
      warnings?: string[];
      submittedAt: Date;
    }
  | {
      action: 'update';
      /** wpIndex of the edited workplace; reconciliation requires manual dismiss. */
      workplaceIndex: number;
      id: number;
      name: string;
      prefix: string;
      restartHint: string;
      warnings?: string[];
      submittedAt: Date;
    }
  | {
      action: 'remove';
      /** id + prefix used to recognise the remove once supervisor restarts. */
      id: number;
      name: string;
      prefix: string;
      workplaceIndex: number;
      restartHint: string;
      warnings?: string[];
      submittedAt: Date;
    };

/** Stable key for dedup / dismiss. */
const pendingChangeKey = (p: PendingWorkplaceChange) => `${p.action}::${p.id}::${p.prefix}`;

/** A disable awaiting confirmation, carrying the number the dialog must quote. */
export interface PendingToggle {
  target: ToggleTarget;
  /** Always false today: enabling needs no confirmation, disabling does. */
  enabled: boolean;
  reapWithinMs: number;
}

interface UseWorkplacesResult {
  payload: WorkplacesPayload | null;
  isLoading: boolean;
  isRefreshing: boolean;
  reqTime: Date | null;
  error: string | null;
  refresh: () => Promise<void>;

  // Service enablement -------------------------------------------------------
  /** Row key currently mid-request or mid-confirmation-read. */
  busyKey: string | null;
  /** Row key to the last thing that happened to it. */
  outcomes: Record<string, ToggleOutcome>;
  /** A disable waiting on its confirmation dialog. */
  pendingToggle: PendingToggle | null;
  /**
   * Entry point from a switch. Enabling applies at once; disabling opens the
   * confirmation, because it stops the process and cannot be undone by hand.
   */
  requestToggle: (target: ToggleTarget, enabled: boolean) => void;
  confirmToggle: (reason: string) => void;
  cancelToggle: () => void;
  dismissOutcome: (rowKey: string) => void;

  // Workplace mutations (genuinely restart-required) --------------------------
  pendingWorkplaceChanges: PendingWorkplaceChange[];
  addWorkplace: (payload: AddWorkplaceRequest) => Promise<AddWorkplacePayload>;
  updateWorkplace: (
    wpIndex: number,
    body: UpdateWorkplaceRequest,
    workplace: { id: number; name: string; prefix: string; lockVersion: number },
  ) => Promise<WorkplaceMutationPayload>;
  removeWorkplace: (
    wpIndex: number,
    reason: string,
    workplace: { id: number; name: string; prefix: string; lockVersion: number },
  ) => Promise<WorkplaceMutationPayload>;
  dismissPendingChange: (key: string) => void;
  restartSupervisor: (reason?: string) => Promise<SupervisorRestartPayload>;
  isRestarting: boolean;
}

/** Find one catalog row by the same identity the path segments use. */
function findRow(
  payload: WorkplacesPayload | null,
  target: ToggleTarget,
): WorkplaceService | GlobalService | undefined {
  if (!payload) return undefined;
  if (target.scope === 'global') {
    return payload.globalServices.find((s) => s.name === target.name);
  }
  return payload.workplaces
    .find((w) => w.index === target.wpIndex)
    ?.services.find((s) => s.name === target.name);
}

/** Replace one row's `effective` in place, returning a new payload object. */
function withRowEffective(
  payload: WorkplacesPayload,
  target: ToggleTarget,
  effective: boolean,
): WorkplacesPayload {
  if (target.scope === 'global') {
    return {
      ...payload,
      globalServices: payload.globalServices.map((row) =>
        row.name === target.name ? { ...row, effective } : row,
      ),
    };
  }
  return {
    ...payload,
    workplaces: payload.workplaces.map((wp) =>
      wp.index !== target.wpIndex
        ? wp
        : {
            ...wp,
            services: wp.services.map((row) =>
              row.name === target.name ? { ...row, effective, workplaceOverride: effective } : row,
            ),
          },
    ),
  };
}

/**
 * State behind the Deployments tab.
 *
 * Two axes live on every catalog row and the hook keeps them apart throughout:
 * `effective` is configured intent (what this deployment is set up to run) and
 * `state` / `running` are live PM2 (what is actually up).
 *
 * The rule everything else follows: THE SWITCH BINDS TO `effective` FROM THE
 * RESPONSE, never to what was requested. The single confirmation read and the
 * enabled-but-not-running notice both exist because a flag being written is not
 * the same as a process being up.
 */
export function useWorkplaces(): UseWorkplacesResult {
  const [payload, setPayload] = useState<WorkplacesPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reqTime, setReqTime] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, ToggleOutcome>>({});
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);
  const [pendingWorkplaceChanges, setPendingWorkplaceChanges] = useState<PendingWorkplaceChange[]>(
    [],
  );
  const [isRestarting, setIsRestarting] = useState(false);

  // Back-off poll timer + a sequence number so a fresh restart cancels any
  // in-flight previous poll without confusing state.
  const restartPollRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; seq: number }>({
    timer: null,
    seq: 0,
  });

  // The payload the confirmation read looks at. State would be stale inside the
  // timeout closure; this is the same value one render later.
  const payloadRef = useRef<WorkplacesPayload | null>(null);
  payloadRef.current = payload;

  // Outstanding timers, cleared on unmount.
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    const timers = timersRef.current;
    return () => {
      aliveRef.current = false;
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const fetchOnce = useCallback(async (initial: boolean) => {
    if (initial) setIsLoading(true);
    else setIsRefreshing(true);
    setError(null);
    try {
      const res = await getWorkplaces();
      const data = res?.data ?? null;
      if (!aliveRef.current) return;
      setPayload(data);
      payloadRef.current = data;
      setReqTime(new Date());
      // Auto-clear pending-change banners when the listing settles into the
      // expected post-restart shape:
      //   add    - a workplace with matching (id, prefix) appears
      //   remove - a workplace with matching (id, prefix) is gone
      //   update - needs manual dismiss; we cannot reliably tell which fields
      //            are "after restart" without storing the submitted patch
      if (data) {
        setPendingWorkplaceChanges((prev) =>
          prev.filter((p) => {
            const exists = data.workplaces.some(
              (w: Workplace) => w.id === p.id && w.prefix === p.prefix,
            );
            if (p.action === 'add') return !exists;
            if (p.action === 'remove') return exists;
            return true;
          }),
        );
      }
    } catch (err) {
      if (!aliveRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch workplaces');
      setPayload(null);
      payloadRef.current = null;
    } finally {
      if (aliveRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchOnce(true);
  }, [fetchOnce]);

  const refresh = useCallback(() => fetchOnce(false), [fetchOnce]);

  const setOutcome = useCallback((rowKey: string, outcome: ToggleOutcome) => {
    setOutcomes((prev) => ({ ...prev, [rowKey]: outcome }));
  }, []);

  const dismissOutcome = useCallback((rowKey: string) => {
    setOutcomes((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, []);

  /**
   * One confirmation read, `appliesWithinMs` after the write.
   *
   * Enabling is not starting. The 200 says the flag is on and the supervisor
   * will try; whether the process came up is only knowable from a later read of
   * `state` / `running`. So we wait the window the response itself quoted (0 on
   * a disable, where the stop already happened in-request) and then report what
   * actually happened, including the case that matters most: enabled, but not
   * running.
   */
  const scheduleConfirmationRead = useCallback(
    (target: ToggleTarget, rowKey: string, appliesWithinMs: number) => {
      const timer = setTimeout(() => {
        timersRef.current.delete(timer);
        fetchOnce(false).then(() => {
          if (!aliveRef.current) return;
          setBusyKey((cur) => (cur === rowKey ? null : cur));
          const row = findRow(payloadRef.current, target);
          if (!row) return;

          // PM2 was never reached, so we cannot claim the process is up OR
          // down. Saying "enabled but not running" here would assert a live
          // fact about a process nobody asked about.
          if (!isLiveStateKnown(row)) {
            setOutcome(rowKey, {
              kind: 'warn',
              message: row.effective
                ? 'Enabled - live state unknown.'
                : 'Disabled - live state unknown.',
              detail: 'PM2 could not be queried, so we cannot confirm whether the process is up.',
            });
            return;
          }

          if (isEnabledButNotRunning(row)) {
            setOutcome(rowKey, {
              kind: 'warn',
              message: 'Enabled, but not running.',
              detail:
                'The flag is on and the supervisor tried to spawn it. A service missing required ' +
                'environment will crash-loop rather than start, so check its process logs.',
            });
            return;
          }

          setOutcome(rowKey, {
            kind: 'ok',
            message: row.effective ? 'Enabled and running.' : 'Disabled - the process is stopped.',
          });
        });
      }, appliesWithinMs);
      timersRef.current.add(timer);
    },
    [fetchOnce, setOutcome],
  );

  const applyToggle = useCallback(
    async (target: ToggleTarget, enabled: boolean, reason: string) => {
      const rowKey = toggleRowKey(target);
      setBusyKey(rowKey);
      dismissOutcome(rowKey);

      const result = await setServiceEnabledOp(target, enabled, reason);
      if (!aliveRef.current) return;

      if (result.kind === 'failed') {
        setBusyKey(null);
        // The flag did not take and nothing was reconciled, so the service is
        // unchanged. Leave the switch exactly where it was: flipping it here
        // would be the lie the read-back contract exists to prevent.
        setOutcome(rowKey, {
          kind: result.unsupported ? 'warn' : 'error',
          message: result.unsupported
            ? 'Not available on this backend yet.'
            : `Could not ${enabled ? 'enable' : 'disable'} ${target.name}.`,
          detail: result.message,
        });
        return;
      }

      // The read-back, and only the read-back.
      const body = result.payload;
      setPayload((prev) => (prev ? withRowEffective(prev, target, body.effective) : prev));

      // A supervisor that could not reconcile answers with the OLD value and
      // says why in `note`. Report that rather than a success.
      if (body.applied === false || body.effective !== enabled) {
        setBusyKey(null);
        setOutcome(rowKey, {
          kind: 'error',
          message: 'The flag did not take - nothing was changed.',
          detail: body.note,
        });
        return;
      }

      const applies =
        typeof body.appliesWithinMs === 'number' ? body.appliesWithinMs : DEFAULT_APPLIES_WITHIN_MS;

      setOutcome(rowKey, {
        kind: 'pending',
        message: body.effective
          ? `Flag is on. Checking in ${formatDelay(applies)} whether the process actually started.`
          : 'Flag is off. Confirming the process stopped.',
        detail: body.note,
      });
      scheduleConfirmationRead(target, rowKey, applies);
    },
    [dismissOutcome, scheduleConfirmationRead, setOutcome],
  );

  const requestToggle = useCallback<UseWorkplacesResult['requestToggle']>(
    (target, enabled) => {
      if (!enabled) {
        // Disabling stops the process now AND cannot be hand-started back: the
        // supervisor re-stops anything that starts a disabled service. That
        // earns a confirmation. Enabling does not - it is undone by turning it
        // back on.
        //
        // The dialog has to quote the reap window BEFORE any response exists,
        // so it uses the default rather than a response field. The operator
        // needs to know what disabling does before they agree to it.
        setPendingToggle({ target, enabled, reapWithinMs: DEFAULT_REAP_WITHIN_MS });
        return;
      }
      applyToggle(target, true, `Enabled ${target.name} from Service Manager`);
    },
    [applyToggle],
  );

  const confirmToggle = useCallback<UseWorkplacesResult['confirmToggle']>(
    (reason) => {
      if (!pendingToggle) return;
      const { target, enabled } = pendingToggle;
      setPendingToggle(null);
      applyToggle(target, enabled, reason || `Disabled ${target.name} from Service Manager`);
    },
    [pendingToggle, applyToggle],
  );

  const cancelToggle = useCallback(() => setPendingToggle(null), []);

  // Workplace mutations. These ARE restart-required: the supervisor answers 202
  // `restartRequired: true` because a new or retired workplace changes the set
  // of processes it manages, which is resolved at boot.

  const addWorkplace = useCallback<UseWorkplacesResult['addWorkplace']>(async (body) => {
    const res = await addWorkplaceCall(body);
    const data: AddWorkplacePayload =
      (res as { data?: AddWorkplacePayload })?.data ?? ({} as AddWorkplacePayload);
    const entry: PendingWorkplaceChange = {
      action: 'add',
      id: body.id,
      name: body.name,
      prefix: body.prefix,
      workplaceIndex: data.workplaceIndex,
      restartHint: data.restartHint || 'Restart the supervisor to activate this workplace.',
      warnings: data.warnings,
      submittedAt: new Date(),
    };
    setPendingWorkplaceChanges((prev) => [
      ...prev.filter((p) => pendingChangeKey(p) !== pendingChangeKey(entry)),
      entry,
    ]);
    return data;
  }, []);

  const updateWorkplace = useCallback<UseWorkplacesResult['updateWorkplace']>(
    async (wpIndex, body, workplace) => {
      const res = await updateWorkplaceCall(wpIndex, body);
      const data: WorkplaceMutationPayload =
        (res as { data?: WorkplaceMutationPayload })?.data ?? ({} as WorkplaceMutationPayload);
      const entry: PendingWorkplaceChange = {
        action: 'update',
        workplaceIndex: data.workplaceIndex ?? wpIndex,
        id: workplace.id,
        // Use the NEW name/prefix if the patch renamed it, so a follow-up
        // add or remove with the new prefix dedups correctly.
        name: body.name?.trim() || workplace.name,
        prefix: body.prefix?.trim() || workplace.prefix,
        restartHint:
          data.restartHint || 'Restart the supervisor to apply changes to this workplace.',
        warnings: data.warnings,
        submittedAt: new Date(),
      };
      setPendingWorkplaceChanges((prev) => [
        ...prev.filter((p) => pendingChangeKey(p) !== pendingChangeKey(entry)),
        entry,
      ]);
      return data;
    },
    [],
  );

  const removeWorkplace = useCallback<UseWorkplacesResult['removeWorkplace']>(
    async (wpIndex, reason, workplace) => {
      const res = await removeWorkplaceCall(wpIndex, {
        reason,
        lockVersion: workplace.lockVersion,
      });
      const data: WorkplaceMutationPayload =
        (res as { data?: WorkplaceMutationPayload })?.data ?? ({} as WorkplaceMutationPayload);
      const entry: PendingWorkplaceChange = {
        action: 'remove',
        id: workplace.id,
        name: workplace.name,
        prefix: workplace.prefix,
        workplaceIndex: data.workplaceIndex ?? wpIndex,
        restartHint: data.restartHint || 'Restart the supervisor to retire this workplace.',
        warnings: data.warnings,
        submittedAt: new Date(),
      };
      setPendingWorkplaceChanges((prev) => [
        ...prev.filter((p) => pendingChangeKey(p) !== pendingChangeKey(entry)),
        entry,
      ]);
      return data;
    },
    [],
  );

  const dismissPendingChange = useCallback<UseWorkplacesResult['dismissPendingChange']>((key) => {
    setPendingWorkplaceChanges((prev) => prev.filter((p) => pendingChangeKey(p) !== key));
  }, []);

  /**
   * Back-off poll after a 202 supervisor restart: start at ~2s, cap at ~10s,
   * stop when the listing fetch succeeds. Healthy supervisors come back within
   * about ten seconds. The seq counter cancels an in-flight poll if a new
   * restart starts before the previous one settles.
   */
  const startRestartPoll = useCallback(() => {
    const slot = restartPollRef.current;
    if (slot.timer) clearTimeout(slot.timer);
    const mySeq = ++slot.seq;
    setIsRestarting(true);
    const delays = [2_000, 4_000, 8_000, 10_000, 10_000, 10_000];
    let attempt = 0;
    const tick = async () => {
      if (mySeq !== slot.seq) return;
      try {
        await fetchOnce(false);
        if (mySeq !== slot.seq) return;
        setIsRestarting(false);
        slot.timer = null;
      } catch {
        if (mySeq !== slot.seq) return;
        if (attempt >= delays.length) {
          setIsRestarting(false);
          slot.timer = null;
          return;
        }
        slot.timer = setTimeout(tick, delays[attempt++]);
      }
    };
    slot.timer = setTimeout(tick, delays[attempt++]);
  }, [fetchOnce]);

  useEffect(() => {
    const slot = restartPollRef.current;
    return () => {
      if (slot.timer) clearTimeout(slot.timer);
      slot.timer = null;
      slot.seq++;
    };
  }, []);

  const restartSupervisor = useCallback<UseWorkplacesResult['restartSupervisor']>(
    async (reason) => {
      const { status, body } = await restartSupervisorCall(reason ?? '');
      const data: SupervisorRestartPayload =
        (body as { data?: SupervisorRestartPayload })?.data ?? ({} as SupervisorRestartPayload);
      if (status === 202) {
        // The pending banners are about to be resolved by the supervisor
        // cycling. Clear them locally and poll so the listing refreshes once
        // the new process answers.
        setPendingWorkplaceChanges([]);
        startRestartPoll();
      }
      // 503 is a deliberate no-op here: the caller surfaces `manualRestartHint`
      // inline so the banner can render the command with a copy button.
      return data;
    },
    [startRestartPoll],
  );

  return {
    payload,
    isLoading,
    isRefreshing,
    reqTime,
    error,
    refresh,
    busyKey,
    outcomes,
    pendingToggle,
    requestToggle,
    confirmToggle,
    cancelToggle,
    dismissOutcome,
    pendingWorkplaceChanges,
    addWorkplace,
    updateWorkplace,
    removeWorkplace,
    dismissPendingChange,
    restartSupervisor,
    isRestarting,
  };
}

export { pendingChangeKey };
